import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Events,
  ItemDeletedResponse,
  Problem,
  ProblemListRepositoryService,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientProblemsStore — clinical problem list for the current patient.
 *
 * Same shape as PatientAllergiesStore: effect-driven auto-load, owns the
 * Pusher subscription for ProblemListProblemListChanged, exposes optimistic
 * add/remove with rollback.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientProblemsStore {
  private patientStore = inject(PatientStore);
  private repo = inject(ProblemListRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<Problem[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.ProblemListProblemListChanged;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Add a problem to the current patient. Optimistic with rollback. The
   * server returns the new id, so the local item's id is patched on success.
   */
  add(problem: Problem): Observable<number> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientProblemsStore.add() called with no current patient');
    }
    const patientId = patient.id;
    const previous = this._items();
    this._items.set([...previous, problem]);
    return this.repo.addProblemToPatient(patientId, problem).pipe(
      tap({
        next: (response: any) => {
          const newId = typeof response === 'number' ? response : response?.value;
          if (typeof newId === 'number') {
            problem.id = newId;
            this._items.set([...this._items()]);
          }
        },
        error: () => this.rollback(previous, patientId)
      }),
      tap({
        next: () => this.reload()
      })
    ) as unknown as Observable<number>;
  }

  /**
   * Remove a problem from the current patient. Optimistic with rollback.
   */
  remove(problemId: number): Observable<ItemDeletedResponse> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientProblemsStore.remove() called with no current patient');
    }
    const patientId = patient.id;
    const previous = this._items();
    this._items.set(previous.filter(p => p.id !== problemId));
    return this.repo.removeProblemFromPatient(patientId, problemId).pipe(
      tap({
        // The repository passes the envelope straight through rather than
        // erroring, so a "successful" response can still mean the server kept
        // the row — roll back on that too, not just on a transport error.
        next: response => {
          if (response?.status?.toLocaleUpperCase() !== 'OK') {
            this.rollback(previous, patientId);
          }
        },
        error: () => this.rollback(previous, patientId)
      })
    );
  }

  /**
   * Restore a pre-mutation snapshot after a failed write, but only if the
   * store is still showing the patient that write was issued for. Without the
   * check, a request that fails after the user has navigated to another
   * patient would put the previous patient's problems on screen.
   */
  private rollback(previous: Problem[], patientId: number): void {
    if (this.patientStore.current()?.id !== patientId) return;
    this._items.set(previous);
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientProblemList(patientId).subscribe({
      next: problems => {
        const sorted = [...problems].sort((a, b) =>
          a.createdDate > b.createdDate ? 1 : -1
        );
        this._items.set(sorted);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient problem list');
        
      }
    });
  }
}
